import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Divider, Stack, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project-imports
import MainCard from 'components/MainCard';
import Avatar from 'components/@extended/Avatar';
import { useHeaderAction } from 'contexts/HeaderActionContext';

// assets
import { ArrowRight2, Buildings2, Home3 } from 'iconsax-react';

// ==============================|| BREADCRUMBS ||============================== //
//
// Layout-level page header rendered once in MainLayout. Resolves the active
// menu item from the current URL and presents a refined header: an indigo-tinted
// rounded icon tile, a small hover-able breadcrumb trail, and the page title.

const Breadcrumbs = ({
  card,
  compact,
  divider = true,
  icon,
  icons,
  maxItems,
  navigation,
  rightAlign,
  separator,
  title,
  titleBottom,
  sx,
  ...others
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { action } = useHeaderAction();
  const [main, setMain] = useState();
  const [item, setItem] = useState();

  const linkSX = {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    '&:hover': { color: theme.palette.primary.main },
  };

  const crumbIconSX = {
    marginRight: theme.spacing(0.5),
    width: '0.9rem',
    height: '0.9rem',
  };

  useEffect(() => {
    navigation?.items?.map((menu) => {
      if (menu.type && menu.type === 'group') {
        getCollapse(menu);
      }
      return false;
    });
  });

  let customLocation = location.pathname;

  // only used for component demo breadcrumbs
  if (customLocation.includes('/components-overview/breadcrumbs')) {
    customLocation = '/apps/kanban/board';
  }

  if (customLocation.includes('/apps/kanban/backlogs')) {
    customLocation = '/apps/kanban/board';
  }

  useEffect(() => {
    if (customLocation.includes('/apps/profiles/user/payment')) {
      setItem(undefined);
    }
  }, [item, customLocation]);

  // set active item state
  const getCollapse = (menu) => {
    if (menu.children) {
      menu.children.filter((collapse) => {
        if (collapse.type && collapse.type === 'collapse') {
          getCollapse(collapse);
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type && collapse.type === 'item') {
          if (customLocation === collapse.url) {
            setMain(menu);
            setItem(collapse);
          }
        }
        return false;
      });
    }
  };

  // item separator
  const SeparatorIcon = separator;
  const separatorIcon = separator ? (
    <SeparatorIcon size={12} />
  ) : (
    <ArrowRight2 size={12} color={theme.palette.text.disabled} />
  );

  // resolve active page (collapse-only sections fall back to `main`)
  const activeCollapse =
    main && main.type === 'collapse' && main.breadcrumbs === true
      ? main
      : undefined;
  const activeItem =
    item && item.type === 'item' && item.breadcrumbs !== false
      ? item
      : undefined;
  const active = activeItem || activeCollapse;

  if (!active) return compact ? null : <Typography />;

  const pageTitle = active.title;
  const ItemIcon = active.icon ? active.icon : Buildings2;

  // breadcrumb trail: Home › [section] › current
  const trail = (
    <MuiBreadcrumbs
      aria-label="breadcrumb"
      maxItems={maxItems || 8}
      separator={separatorIcon}
    >
      <Typography
        component={Link}
        to="/"
        variant="caption"
        color="text.secondary"
        sx={linkSX}
      >
        {icons && <Home3 style={crumbIconSX} />}
        {icon && !icons && (
          <Home3 variant="Bold" style={{ ...crumbIconSX, marginRight: 0 }} />
        )}
        {(!icon || icons) && 'Home'}
      </Typography>
      {activeCollapse && activeItem && (
        <Typography
          component={Link}
          to={activeCollapse.url || document.location.pathname}
          variant="caption"
          color="text.secondary"
          sx={linkSX}
        >
          {activeCollapse.title}
        </Typography>
      )}
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ fontWeight: 600 }}
      >
        {pageTitle}
      </Typography>
    </MuiBreadcrumbs>
  );

  // Compact variant: just the trail, sized to sit inside the app-bar toolbar.
  // The page title is the last (bold) crumb, so no separate heading is needed.
  if (compact) {
    return (
      <Box
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
          '& .MuiBreadcrumbs-li': { whiteSpace: 'nowrap' },
          ...sx,
        }}
      >
        {trail}
      </Box>
    );
  }

  const heading = title && (
    <Typography
      variant="h3"
      sx={{ fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.2 }}
    >
      {pageTitle}
    </Typography>
  );

  const breadcrumbContent = (
    <MainCard
      border={card}
      sx={
        card === false
          ? { mb: 3, bgcolor: 'transparent', ...sx }
          : { mb: 3, ...sx }
      }
      {...others}
      content={card}
      boxShadow={false}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {title && (
            <Avatar
              variant="rounded"
              color="primary"
              size="lg"
              sx={{
                flexShrink: 0,
                boxShadow: `0 6px 16px ${alpha(
                  theme.palette.primary.main,
                  0.22
                )}`,
              }}
            >
              <ItemIcon variant="Bulk" />
            </Avatar>
          )}
          <Stack spacing={0.75}>
            {!titleBottom && heading}
            {trail}
            {titleBottom && heading}
          </Stack>
        </Stack>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
      {card === false && divider !== false && <Divider sx={{ mt: 2.5 }} />}
    </MainCard>
  );

  return breadcrumbContent;
};

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  compact: PropTypes.bool,
  divider: PropTypes.bool,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  maxItems: PropTypes.number,
  navigation: PropTypes.object,
  rightAlign: PropTypes.bool,
  separator: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default Breadcrumbs;
